<script setup>
import { ref, computed } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import ComboBoxDropdown from 'dashboard/components-next/combobox/ComboBoxDropdown.vue';

const props = defineProps({
  // Selected role values.
  modelValue: {
    type: Array,
    default: () => [],
  },
  // Role vocabulary as [{ value, label }].
  options: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const open = ref(false);
const search = ref('');

const labelFor = value =>
  props.options.find(option => option.value === value)?.label || value;

const filteredOptions = computed(() => {
  const term = search.value.toLowerCase();
  return props.options.filter(option =>
    option.label.toLowerCase().includes(term)
  );
});

const toggleOption = option => {
  const next = new Set(props.modelValue);
  if (next.has(option.value)) next.delete(option.value);
  else next.add(option.value);
  emit('update:modelValue', [...next]);
};

const toggleDropdown = () => {
  open.value = !open.value;
  if (open.value) search.value = '';
};
</script>

<template>
  <div class="relative min-w-0">
    <OnClickOutside @trigger="open = false">
      <button
        type="button"
        class="flex flex-wrap items-center w-full gap-1 px-1.5 py-1 -mx-1.5 text-left transition-colors rounded-md min-h-[28px] hover:bg-n-alpha-2"
        @click="toggleDropdown"
      >
        <span
          v-for="role in modelValue"
          :key="role"
          class="px-1.5 py-0.5 text-xs rounded bg-n-alpha-2 text-n-slate-12"
        >
          {{ labelFor(role) }}
        </span>
        <span v-if="!modelValue.length" class="text-xs text-n-slate-9">
          {{ t('PROJECTS.ROLES.ADD') }}
        </span>
      </button>

      <ComboBoxDropdown
        v-model:search-value="search"
        :open="open"
        :options="filteredOptions"
        :selected-values="modelValue"
        multiple
        @select="toggleOption"
      />
    </OnClickOutside>
  </div>
</template>
